import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

interface SessionContextType {
  sessionId: string | undefined;
  setCurrentSessionId: (sessionId: string | undefined) => void;
}

export const SessionContext = createContext<SessionContextType | undefined>(
  undefined,
);

export const useSessionContext = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSessionContext must be used within a SessionProvider");
  }
  return context;
};

type SessionProviderProps = {
  initialSessionId?: string;
  children: React.ReactNode;
};

export const SessionProvider = (props: SessionProviderProps) => {
  const [sessionId, setCurrentSessionId] = useState<string | undefined>(
    props.initialSessionId,
  );

  useEffect(() => {
    setCurrentSessionId(props.initialSessionId);
  }, [props.initialSessionId]);

  const value = useMemo(
    () => ({ sessionId, setCurrentSessionId }),
    [sessionId, setCurrentSessionId],
  );

  return (
    <SessionContext.Provider value={value}>
      {props.children}
    </SessionContext.Provider>
  );
};

export function withSessionProvider<P extends object>(
  Component: React.ComponentType<P>,
  props?: Omit<SessionProviderProps, "children">,
): React.FC<P> {
  return function WrappedWithSessionProvider(p: P) {
    return (
      <SessionProvider {...props}>
        <Component {...p} />
      </SessionProvider>
    );
  };
}
